import { useState } from 'react';
import { api } from '../../api/client';
import type { AnalystThreadDetail, AnalystThreadSummary } from '../../api/types';
import { color, fontSize, radius } from '../../theme/tokens';

/**
 * The admin transcript viewer (spec 006 T115, Constitution 2.0.0 Principle V).
 *
 * ## ⚠ The SECOND of exactly two admin capabilities
 *
 * The amendment enumerates them: user management, and read-only access to analyst transcripts. A
 * third would need another amendment, not a component.
 *
 * ## ⚠ Three bounds, all of them part of the rule
 *
 *  - **read-only** — nothing here edits, annotates or deletes another person's thread
 *  - **transcript TEXT only** — a thread holds no rows by construction, so this surface is never
 *    a route to candidate contact detail, and an expired result stays expired for an admin too
 *  - **every use is logged** — the server writes an audit entry on each read. Reading is not a
 *    mutation, so nothing existing covered it; that record is the whole compensating control
 *
 * ## ⚠ It looks a named person up rather than browsing everyone
 *
 * The owner address is required. "Show me what everyone has been asking" is a materially bigger
 * power than "show me this person's threads", and the amendment granted the second.
 */
export function AdminThreadViewer() {
  const [owner, setOwner] = useState('');
  const [threads, setThreads] = useState<AnalystThreadSummary[] | null>(null);
  const [detail, setDetail] = useState<AnalystThreadDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function lookUp(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setDetail(null);
    try {
      const result = await api.analystAdminThreads(owner.trim());
      setThreads(result.threads);
    } catch {
      setError('Could not load that person’s conversations.');
    }
  }

  return (
    <section style={{ marginTop: 24 }}>
      <h2 style={{ fontSize: fontSize.captionStrong, color: color.ink, margin: '0 0 4px' }}>
        Analyst conversations
      </h2>
      <p style={{ fontSize: fontSize.caption, color: color.inkMuted, margin: '0 0 10px' }}>
        Look up one person’s analyst conversations. {/* ⚠ Stated on the surface, because a reading
        power the subject does not know about is a different thing from oversight. */}
        Every time you open one, it is recorded with your name and theirs.
      </p>

      <form onSubmit={lookUp} style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input
          value={owner}
          onChange={(event) => setOwner(event.target.value)}
          placeholder="person@cynetcorp.com"
          aria-label="Whose conversations to look up"
          style={{
            flex: 1,
            border: `1px solid ${color.border}`,
            borderRadius: radius.lg,
            padding: '7px 10px',
            fontSize: fontSize.caption,
            color: color.ink,
            background: color.surface,
          }}
        />
        <button
          type="submit"
          style={{
            border: `1px solid ${color.border}`,
            borderRadius: radius.lg,
            padding: '7px 12px',
            background: color.surfaceSubtle,
            color: color.ink,
            cursor: 'pointer',
            fontSize: fontSize.caption,
          }}
        >
          Look up
        </button>
      </form>

      {error ? (
        <p style={{ fontSize: fontSize.caption, color: color.inkMuted }}>{error}</p>
      ) : null}

      {threads?.length === 0 ? (
        <p style={{ fontSize: fontSize.caption, color: color.inkMuted }}>
          No conversations for that address.
        </p>
      ) : null}

      {threads?.map((thread) => (
        <button
          key={thread.threadId}
          type="button"
          onClick={() => {
            void api.analystAdminThread(thread.threadId).then(setDetail);
          }}
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            border: `1px solid ${color.borderSubtle}`,
            borderRadius: radius.md,
            padding: '7px 10px',
            marginBottom: 6,
            background: color.surface,
            color: color.ink,
            cursor: 'pointer',
            fontSize: fontSize.caption,
          }}
        >
          {thread.title ?? 'Conversation'} ·{' '}
          {new Date(thread.lastTurnAt).toLocaleDateString('en-US', { timeZone: 'UTC' })}
          {thread.frozen ? ' · frozen' : ''}
        </button>
      ))}

      {detail ? (
        <div
          style={{
            marginTop: 10,
            padding: 10,
            border: `1px solid ${color.borderSubtle}`,
            borderRadius: radius.md,
            background: color.surfaceSubtle,
          }}
        >
          {detail.turns.map((turn) => (
            <div key={turn.turnId} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: fontSize.captionStrong, color: color.ink }}>{turn.question}</div>
              {turn.prose ? (
                <div style={{ fontSize: fontSize.caption, color: color.inkMuted }}>{turn.prose}</div>
              ) : null}
              {/*
                ⚠ A thread holds text only, so there is never a table here — and an expired
                result stays expired for an admin. Oversight does not widen what a transcript
                holds.
              */}
              {turn.resultExpired ? (
                <div style={{ fontSize: fontSize.caption, color: color.inkHint }}>
                  Rows are not kept with a conversation.
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
